// React imports
import { UploadOutlined } from "@ant-design/icons"
import { CurrencyEnum, Employee, RolesEnum, rolesStringArray, Team, UploadKindEnum, UserApprover } from "@finway-group/shared/lib/models"
// Ant design imports
import { Button, Col, Form, Input, Row, Select, Upload, Tooltip } from "antd"
import { FormInstance } from "antd/lib/form/Form"
import { Store } from "antd/lib/form/interface"
import { RcCustomRequestOptions, RcFile } from "antd/lib/upload/interface"
import UserImage from "Components/UserImage"
import React, { useCallback, useEffect, useState } from "react"
// Other imports
import { useTranslation } from "react-i18next"
import PhoneInput from "react-phone-number-input"
import "react-phone-number-input/style.css"
import { FINWAY_ADMIN_EMAIL } from "Shared/config/consts"
import { useCompanies, useCompany } from "Shared/hooks/company.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useTeams } from "Shared/hooks/team.hooks"
// Model imports
import { CurrencyService, EmployeeService, FileService, UserService } from "Shared/services"
import { getPopupAnchor } from "Shared/utils"
import { BLUE, WHITE } from "App.scss"
// Imports of shared modules
import {
    checkFileSize,
    determinePossibleApprovers,
    doFilterSelect,
    getDisplayCountryCallingCode,
    getIBANPrintFormat,
    validateCreditorNumberDATEVCompatibility,
} from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import validateEmail from "Shared/validators/email.validator"
import { SECONDARY } from "App.scss"
import { EmployeeRules } from "../rules"
import EmployeeLimitForm from "./employeeLimit.form"
import moment from "moment-timezone"
import { useIsFreeVersion, useIsTravelEnabled } from "Shared/hooks/featureFlags.hooks"
import { AlertCircle } from "react-feather"
import { getTooltipPopupContainer } from "Shared/utils/helpers"
import { isAdmin } from "Shared/utils/filter.utils"

const { Option } = Select

interface EmployeeFormInterface {
    form: FormInstance
    isNew: boolean
    isInviteMode?: boolean
    employee?: Employee
    onSubmit: (values: Store) => any
    onRestore: (employee: Employee) => any
    setIsUploading: (flag: boolean) => void
    setIsInviteMode: (inviteMode: boolean) => void
}

export const EmployeeForm: React.FC<EmployeeFormInterface> = ({ form, isNew, employee, isInviteMode, onSubmit, onRestore, setIsUploading, setIsInviteMode }) => {
    const { t } = useTranslation()

    const [phoneNumber, setPhoneNumber] = useStateIfMounted(form.getFieldValue("workPhone"))
    const [team, setTeam] = useStateIfMounted<string>("")
    const [approverList, setApproverList] = useStateIfMounted<Employee[]>([])
    const [approvalStatus, setApprovalStatus] = useStateIfMounted<boolean>(true)
    const [superior, setSuperior] = useStateIfMounted<string>("")
    const [fileList, setFileList] = useStateIfMounted<any[]>([])
    const [email, setEmail] = useState(employee?.email || "")
    const [approvalLimits, setApprovalLimits] = useStateIfMounted<UserApprover[]>([])
    const [phoneInputPlaceholder, setPhoneInputPlaceholder] = useState(getDisplayCountryCallingCode())
    const [emailExist, setEmailExist] = useStateIfMounted(false)
    const [emailUserDeleted, setEmailUserDeleted] = useStateIfMounted(false)
    const [userToRestore, setUserToRestore] = useStateIfMounted<Employee>(new Employee())

    // feature flags
    const isFreeVersion = useIsFreeVersion()
    const isTravelEnabled = useIsTravelEnabled()

    const employees = useEmployees(true, false, true)
    const teams = useTeams()
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const selectedCompany = useCompany()

    const globalCurrency = loggedInUser?.settings.globalCurrency
    const isMe = employee && loggedInUser.id === employee.id

    const [creditorNumberInfo, setCreditorNumberInfo] = useStateIfMounted<string | undefined>(undefined)

    const onCreditorNumberChange = (e: any) => {
        try {
            if (e.target.value) validateCreditorNumberDATEVCompatibility(e.target.value)
            setCreditorNumberInfo(undefined)
        } catch (err) {
            setCreditorNumberInfo(t("error:vendor.incompatible_creditor_number"))
        }
    }

    useEffect(() => {
        let isMounted = true

        if (!isNew && employee) {
            if (employee.img) {
                const currentFileList = [
                    {
                        uid: "1",
                        name: employee.getFullName(),
                        status: "done",
                        thumbUrl: employee.img,
                        type: "image/png",
                    },
                ]
                setFileList(currentFileList)
            }
            setPhoneNumber(employee.workPhone)
            setTeam(employee.activeCompanyProfile.team)
            if (employee.activeCompanyProfile.superior) setSuperior(employee.activeCompanyProfile.superior)
            if (employee.activeCompanyProfile.approvals) {
                setApprovalLimits(
                    employee?.activeCompanyProfile.approvals?.map(
                        (approval: UserApprover) =>
                            new UserApprover({
                                ...approval,
                                limit: approval.currency === globalCurrency ? approval.limit : CurrencyService.convert(approval.limit, approval.currency, globalCurrency),
                                currency: globalCurrency,
                            })
                    )
                )
            }
        }

        if (isNew) {
            EmployeeService.getLatestEmployeeCreditorNumber().then((creditorNumber) => {
                if (isMounted) form.setFieldsValue({ creditorNumber: creditorNumber })
            })
        }

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const newPossibleApprovers = determinePossibleApprovers(employee?.email || "", employees, team)
        // add employees that are already set previously, but potentially not in the possible
        // approver list anymore. Needed as it would only show IDs otherwise
        const presetApprovers = [
            ...(employee?.activeCompanyProfile.superior ? [EmployeeService.getEmployeeById(employee.activeCompanyProfile.superior)] : []),
            ...(employee?.activeCompanyProfile.approvals
                ? employee?.activeCompanyProfile.approvals.map((approval: UserApprover) => EmployeeService.getEmployeeById(approval.approver))
                : []),
        ]
        setApproverList([
            ...newPossibleApprovers,
            ...presetApprovers.filter((presetApprover) => !newPossibleApprovers.find((newPossibleApprover) => newPossibleApprover.id === presetApprover.id)),
        ])
    }, [team])

    const rules = EmployeeRules(approverList, form)

    const doFileUpload = async (options: RcCustomRequestOptions) => {
        const { onSuccess, onError, onProgress, file } = options
        if (!checkFileSize(file)) return

        try {
            setIsUploading(true)
            if (employee) employee.img = ""
            const data = await FileService.upload(file, UploadKindEnum.USER_IMG.toString(), onProgress)
            setIsUploading(false)
            onSuccess(data, data.url as unknown as RcFile)
        } catch (err) {
            setIsUploading(false)
            onError(err)
        }
    }

    const handlePhoneNumberChange = (value: string) => {
        form.setFieldsValue({
            workPhone: value,
        })
        return {
            workPhone: value,
        }
    }

    const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const ibanPrintFormat = getIBANPrintFormat(e.target.value)

        form.setFieldsValue({
            iban: ibanPrintFormat,
        })

        return {
            iban: ibanPrintFormat,
        }
    }

    const handleCountryChange = (value: any) => {
        if (value) {
            const displayCountryCallingCode = getDisplayCountryCallingCode(value)
            setPhoneInputPlaceholder(displayCountryCallingCode)
        }
    }

    const verifyEmail = useCallback(async () => {
        const emailValue = form.getFieldValue("email")
        if (validateEmail(emailValue)) {
            setEmail(emailValue)
            setEmailExist(false)
            setEmailUserDeleted(false)

            try {
                const employee = await EmployeeService.getOneByEmail(emailValue)
                const employeeCompanyProfile = employee.getUserCompanyProfile(selectedCompany._id)
                const isPartOfSelectedCompany = employeeCompanyProfile?.companyId
                const isUserDeleted = isPartOfSelectedCompany && employeeCompanyProfile?.deleted

                if (isPartOfSelectedCompany) {
                    if (!isUserDeleted) throw Error("User already exist")
                    if (isUserDeleted) {
                        setUserToRestore(employee)
                        throw Error("User deleted")
                    }
                }
                setIsInviteMode(true)
            } catch (error) {
                if (error.message === "User already exist") setEmailExist(true)
                if (error.message === "User deleted") setEmailUserDeleted(true)
                setIsInviteMode(false)
            }

            return
        }

        setIsInviteMode(false)
    }, [])

    const handleRestoreUser = async () => {
        userToRestore.companyProfiles = userToRestore?.companyProfiles?.map((profile) => {
            if (profile.companyId === selectedCompany._id) {
                profile.deleted = false
                return profile
            }

            return profile
        })

        onRestore(userToRestore)
    }

    return (
        <div>
            <Form form={form} layout="vertical" onFinish={onSubmit} scrollToFirstError>
                <Row gutter={[16, 16]}>
                    {!isInviteMode && (
                        <>
                            <Col span={12} sm={24} md={12}>
                                <Form.Item label={t("input:employee.first_name")} name="firstName" rules={rules.firstName}>
                                    <Input placeholder={t("placeholder:employee.first_name")} autoComplete="nope" />
                                </Form.Item>
                            </Col>
                            <Col span={12} sm={24} md={12}>
                                <Form.Item label={t("input:employee.last_name")} name="lastName" rules={rules.lastName}>
                                    <Input placeholder={t("placeholder:employee.last_name")} autoComplete="nope" />
                                </Form.Item>
                            </Col>
                        </>
                    )}
                    <Col span={24}>
                        <Form.Item className="mb-10" name="email" label={t("input:employee.email")} required rules={rules.email}>
                            <Input type="email" name="email" placeholder={t("placeholder:employee.email")} onChange={verifyEmail} autoComplete="nope" />
                        </Form.Item>
                        {emailExist && <span className="text-error">{t("error:employee.exists")}</span>}
                        {emailUserDeleted && (
                            <span className="text-error">
                                {t("confirm:employee.restore.message")} <a onClick={handleRestoreUser}>{t("action:employee.restore")}</a>{" "}
                            </span>
                        )}
                        {!isNew && email !== employee?.email && <p className="ant-form-item-has-error -mt-10">{t("info:email_update")}</p>}
                    </Col>

                    {!isInviteMode && (
                        <>
                            <Col span={24}>
                                <Form.Item label={t("input:employee.location")} name="location" rules={rules.location}>
                                    <Input placeholder={t("placeholder:employee.location")} autoComplete="nope" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item label={t("input:employee.timezone")} name="timeZone" required>
                                    <Select showSearch filterOption={doFilterSelect} getPopupContainer={getPopupAnchor()}>
                                        {moment.tz.names().map((name, index) => (
                                            <Select.Option key={index} label={name} value={name}>
                                                {name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item label={t("input:employee.work_phone")} name="workPhone" rules={rules.workPhone} getValueFromEvent={handlePhoneNumberChange}>
                                    <Input
                                        hidden
                                        addonAfter={
                                            <PhoneInput
                                                key="workPhone"
                                                placeholder={phoneInputPlaceholder}
                                                onCountryChange={handleCountryChange}
                                                value={phoneNumber}
                                                onChange={handlePhoneNumberChange}
                                            ></PhoneInput>
                                        }
                                    ></Input>
                                </Form.Item>
                            </Col>
                            <Col span={24} md="20">
                                <Form.Item label={t("input:employee.profile_picture")} name="img" shouldUpdate>
                                    <Upload
                                        listType="picture"
                                        accept=".jpg,.jpeg,.png"
                                        customRequest={doFileUpload}
                                        multiple={false}
                                        fileList={fileList}
                                        onChange={(data) => setFileList(data.fileList)}
                                        onRemove={() => setFileList([])}
                                        // to avoid opening the URL on click
                                        onPreview={() => {}}
                                        progress={{
                                            strokeWidth: 3,
                                            showInfo: false,
                                            strokeColor: SECONDARY,
                                            size: "small",
                                            className: "pr-20",
                                        }}
                                    >
                                        {fileList.length === 0 && (
                                            <Button className="text-left btn-default">
                                                <UploadOutlined />
                                                <span> {t("action:upload_profile_picture")}</span>
                                            </Button>
                                        )}
                                    </Upload>
                                </Form.Item>
                            </Col>
                            {(loggedInUser.activeCompanyProfile.isAdmin || isMe) && (
                                <>
                                    <Col span={14} md="14">
                                        <Form.Item label={t("input:vendor.iban")} name="iban" rules={rules.iban}>
                                            <Input onChange={handleIBANChange} placeholder={t("placeholder:vendor.iban")} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={10} md="10">
                                        <Form.Item label={t("input:vendor.bic")} name="bic" rules={rules.bic}>
                                            <Input placeholder={t("placeholder:vendor.bic")} />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}
                            {!isFreeVersion && loggedInUser.activeCompanyProfile.isAdmin && (
                                <React.Fragment>
                                    <Col span={12} md="24">
                                        <Form.Item
                                            label={t("input:vendor.creditor_number")}
                                            name="creditorNumber"
                                            help={creditorNumberInfo}
                                            validateStatus={creditorNumberInfo ? "warning" : undefined}
                                        >
                                            <Input placeholder={t("placeholder:vendor.creditor_number")} onChange={onCreditorNumberChange} />
                                        </Form.Item>
                                    </Col>
                                </React.Fragment>
                            )}

                            {isTravelEnabled && isAdmin(loggedInUser) && (
                                <Col span={12} md="24">
                                    <Form.Item
                                        label={
                                            <>
                                                {t("input:employee.mileage_rate")}
                                                <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("info:employee_mileage_rate")}>
                                                    <AlertCircle className="mt-2" size={16} fill={BLUE} stroke={WHITE} />
                                                </Tooltip>
                                            </>
                                        }
                                        name="mileageRate"
                                        rules={rules.mileageRate}
                                    >
                                        <Input placeholder={t("placeholder:employee.mileage_rate")} />
                                    </Form.Item>
                                </Col>
                            )}

                            {loggedInUser.activeCompanyProfile.isAdmin && (
                                <Col span={24}>
                                    <Form.Item label={t("input:employee.position")} name="position" rules={rules.position}>
                                        <Input placeholder={t("placeholder:employee.position")} autoComplete="nope" />
                                    </Form.Item>
                                </Col>
                            )}
                        </>
                    )}

                    {loggedInUser.activeCompanyProfile.isAdmin && (
                        <>
                            <Col span={24}>
                                <Form.Item name="team" label={t("input:employee.team")} rules={rules.team} required={true}>
                                    <Select showSearch filterOption={doFilterSelect} onChange={(value) => setTeam(value.toString())} getPopupContainer={getPopupAnchor()}>
                                        {teams
                                            .filter((team: Team) => !team.deleted || team._id == employee?.activeCompanyProfile?.team)
                                            .map((entry: Team, index: number) => (
                                                <Select.Option disabled={entry.deleted} key={index} label={entry.teamName} value={entry._id}>
                                                    {entry.teamName}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                {/** Hiding the role form and sets the value to admin for free version */}
                                <Form.Item
                                    name="role"
                                    label={t("input:employee.role")}
                                    rules={rules.role}
                                    required={true}
                                    initialValue={isFreeVersion ? RolesEnum.ADMIN : RolesEnum.EMPLOYEE}
                                    className={isFreeVersion ? "hidden" : ""}
                                >
                                    <Select id="role" getPopupContainer={getPopupAnchor()}>
                                        {rolesStringArray.map((role, index) => (
                                            <Option key={index} value={index} label={t(`input:employee.roles.${role.toLowerCase()}`)}>
                                                <span id={`role-option-${index}`}>{t(`input:employee.roles.${role.toLowerCase()}`)}</span>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item name="superior" label={t("input:employee.superior")} initialValue={superior}>
                                    <Select showSearch filterOption={doFilterSelect} onChange={(value: string) => setSuperior(value)} getPopupContainer={getPopupAnchor()}>
                                        <Option label={t("missing:employee.superior")} value="">
                                            <div className="flex items-center" id={`superior-option-0`}>
                                                <span>{t("missing:employee.superior")}</span>
                                            </div>
                                        </Option>
                                        {approverList
                                            .filter(({ email }: Employee) => email !== FINWAY_ADMIN_EMAIL)
                                            .map((employee: Employee, index: number) => {
                                                return employee.id ? (
                                                    <Option key={index} label={`${employee.firstName} ${employee.lastName}`} value={employee.id}>
                                                        <div className="flex items-center" id={`superior-option-${index + 1}`}>
                                                            <UserImage user={employee} />
                                                            <span>{`${employee.firstName} ${employee.lastName}`}</span>
                                                        </div>
                                                    </Option>
                                                ) : null
                                            })}
                                    </Select>
                                </Form.Item>
                            </Col>

                            {!isFreeVersion && (
                                <>
                                    <Col span={24}>
                                        <hr className="divider" />
                                        <p className="font-bold text-lg mb-20 text-text-dark">{t("label:limits")}</p>
                                        {approvalStatus && <div className="mb-10">{t("missing:limits")}</div>}
                                        <EmployeeLimitForm
                                            globalCurrency={globalCurrency}
                                            superior={superior}
                                            possibleApprovers={approverList}
                                            setListLength={(limit) => setApprovalStatus(limit < 0)}
                                            formInstance={form}
                                            approvals={approvalLimits}
                                            rules={rules}
                                        />
                                    </Col>
                                </>
                            )}
                        </>
                    )}
                </Row>
            </Form>
        </div>
    )
}

export default EmployeeForm
